import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Handshake, Palette, CheckCircle, Ship, Globe, Briefcase, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AboutPage = () => {
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error("Please fill in all fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/preorders`, {
        product_id: "general-enquiry",
        product_name: "General Enquiry",
        customer_name: contactForm.name,
        email: contactForm.email,
        message: contactForm.message
      });
      toast.success("Thank you! We'll get back to you soon.");
      setContactForm({ name: "", email: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    {
      number: "01",
      title: "Discovery & Consultation",
      subtitle: "Understand your vision",
      icon: Briefcase,
      description: "Meet with the client to discuss project requirements, product categories, target pricing, quality expectations, quantities, timelines, and customization needs.",
      image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&q=80"
    },
    {
      number: "02",
      title: "Sourcing & Supplier Selection",
      subtitle: "Finding the right artisan partners",
      icon: Handshake,
      description: "We identify and evaluate trusted artisans and manufacturers across India who best match your requirements.",
      image: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=600&q=80"
    },
    {
      number: "03",
      title: "Product Development & Production",
      subtitle: "Turning ideas into reality",
      icon: Palette,
      description: "During production, we work closely with both the client and suppliers to ensure every detail meets expectations.",
      image: "https://images.unsplash.com/photo-1452860606245-08befc0ff44b?w=600&q=80"
    },
    {
      number: "04",
      title: "Quality Assurance & Compliance",
      subtitle: "Ensuring every shipment meets global standards",
      icon: CheckCircle,
      description: "Before dispatch, every order undergoes rigorous quality checks and compliance verification to minimize risks.",
      image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&q=80"
    },
    {
      number: "05",
      title: "Global Shipping & Delivery",
      subtitle: "From India to your doorstep",
      icon: Ship,
      description: "Once approved, we coordinate international logistics to ensure safe and timely delivery.",
      image: "https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=600&q=80"
    }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="about-page">
      <Header />

      {/* Hero Section */}
      <section className="relative py-24 md:py-32 border-b border-border/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="text-xs font-medium uppercase tracking-[0.2em] text-primary">
              About Us
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-foreground mt-4 leading-tight">
              Where Global Homes Meet <span className="text-primary">Artisan Craft</span>
            </h1>
            <p className="text-foreground/60 mt-8 text-lg leading-relaxed max-w-2xl">
              We bridge the gap between exceptional Indian craftsmanship and discerning global buyers, 
              ensuring every piece tells a story of quality, tradition, and innovation.
            </p>
          </div>
        </div>
      </section>

      {/* Founder Section - Now above Our Process */}
      <section className="py-24 md:py-32 border-b border-border/30 bg-card">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-xs font-medium uppercase tracking-[0.2em] text-primary">
                Leadership
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground mt-4">
                Meet the Founder
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="p-6 rounded-lg bg-background border border-border/30">
                <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center mb-4">
                  <Briefcase className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading text-lg text-foreground font-medium">Industry Experience</h3>
                <p className="text-foreground/60 mt-3 leading-relaxed text-sm">
                  Over 20 years of extensive experience across multiple Buying Houses, spanning Textiles, 
                  Home Décor, Seasonal Décor (Christmas, Spring, Harvest, Halloween), Hard Goods, 
                  and Outdoor & Indoor Furniture.
                </p>
              </div>

              <div className="p-6 rounded-lg bg-background border border-border/30">
                <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center mb-4">
                  <Globe className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading text-lg text-foreground font-medium">Global Expertise</h3>
                <p className="text-foreground/60 mt-3 leading-relaxed text-sm">
                  Proven track record sourcing for companies in USA, Spain, UK, Japan, Italy, and Netherlands, 
                  serving mid-level to high-volume buying companies across international markets.
                </p>
              </div>

              <div className="p-6 rounded-lg bg-background border border-border/30">
                <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center mb-4">
                  <Handshake className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading text-lg text-foreground font-medium">Service Excellence</h3>
                <p className="text-foreground/60 mt-3 leading-relaxed text-sm">
                  Currently operating as a dedicated service provider, delivering seamless sourcing 
                  solutions to international companies seeking reliable partnerships in India.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-20">
            <span className="text-xs font-medium uppercase tracking-[0.2em] text-primary">
              Our Process
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground mt-4">
              A Transparent Journey from Concept to Delivery
            </h2>
            <p className="text-foreground/60 mt-6 text-lg leading-relaxed max-w-3xl mx-auto">
              At Artisan Bridge Home, we simplify global sourcing by managing every stage—from understanding 
              your requirements to delivering exceptional handcrafted products to your destination. Our structured 
              process ensures quality, transparency, and reliable communication throughout your sourcing journey.
            </p>
          </div>

          <div className="space-y-24">
            {steps.map((step, index) => (
              <div 
                key={step.number}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                data-testid={`process-step-${step.number}`}
              >
                {/* Content */}
                <div className={`space-y-6 ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="flex items-center gap-4">
                    <span className="text-5xl font-heading text-primary/30 font-light">
                      {step.number}
                    </span>
                    <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center">
                      <step.icon className="w-6 h-6 text-primary" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl md:text-3xl text-foreground">
                      {step.title}
                    </h3>
                    <p className="text-primary text-sm font-medium tracking-wide mt-1">
                      {step.subtitle}
                    </p>
                  </div>
                  <p className="text-foreground/60 leading-relaxed text-lg">
                    {step.description}
                  </p>
                </div>

                {/* Image */}
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="relative rounded-lg overflow-hidden aspect-[4/3]">
                    <img
                      src={step.image}
                      alt={step.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/20 via-transparent to-transparent" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why This Works Section */}
      <section className="py-24 md:py-32 border-t border-border/30 bg-card">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-xs font-medium uppercase tracking-[0.2em] text-primary">
              Why This Works
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground mt-4">
              Your Trusted Sourcing Partner
            </h2>
            <p className="text-foreground/60 mt-8 text-lg leading-relaxed">
              This flow positions Artisan Bridge Home as a full-service sourcing partner, not just a marketplace. 
              We clearly communicate that we manage the entire lifecycle—consultation, sourcing, production, 
              quality assurance, and international logistics—which builds confidence with overseas buyers 
              looking for a reliable partner in India.
            </p>
            <div className="grid md:grid-cols-3 gap-8 mt-16">
              <div className="p-6 rounded-lg bg-background border border-border/30">
                <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading text-lg text-foreground">End-to-End Management</h3>
                <p className="text-foreground/60 text-sm mt-2">From concept to delivery, we handle every step</p>
              </div>
              <div className="p-6 rounded-lg bg-background border border-border/30">
                <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Handshake className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading text-lg text-foreground">Trusted Partnerships</h3>
                <p className="text-foreground/60 text-sm mt-2">Vetted artisans and manufacturers across India</p>
              </div>
              <div className="p-6 rounded-lg bg-background border border-border/30">
                <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Ship className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading text-lg text-foreground">Global Reach</h3>
                <p className="text-foreground/60 text-sm mt-2">Reliable international logistics and compliance</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Us Section */}
      <section className="py-24 md:py-32 border-t border-border/30" id="contact">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left - Text */}
            <div>
              <span className="text-xs font-medium uppercase tracking-[0.2em] text-primary">
                Get In Touch
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground mt-4">
                Contact Us
              </h2>
              <p className="text-foreground/60 mt-6 leading-relaxed">
                Have a project in mind or want to learn more about our sourcing capabilities? 
                Send us a message and our team will get back to you within 24 hours.
              </p>
              <div className="mt-8 space-y-4">
                <p className="text-foreground/60 text-sm">
                  <span className="text-foreground font-medium">Email:</span> hello@artisanbridgehome.com
                </p>
                <p className="text-foreground/60 text-sm">
                  <span className="text-foreground font-medium">Location:</span> Delhi, India
                </p>
              </div>
            </div>

            {/* Right - Form */}
            <div className="bg-card border border-border rounded-2xl p-8">
              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="contact-name">Your Name *</Label>
                  <Input
                    id="contact-name"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                    placeholder="Enter your name"
                    className="h-12 rounded-lg bg-muted/50 border-transparent focus:border-primary focus:bg-background"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact-email">Email Address *</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    placeholder="your@email.com"
                    className="h-12 rounded-lg bg-muted/50 border-transparent focus:border-primary focus:bg-background"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact-message">Your Message *</Label>
                  <Textarea
                    id="contact-message"
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    placeholder="Tell us about your project or requirements..."
                    rows={5}
                    className="rounded-lg bg-muted/50 border-transparent focus:border-primary focus:bg-background resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Send Message"
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
